import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getFirestore,
  collection,
  query,
  orderBy,
  getDocs,
  getDoc,
  doc,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { toast } from 'react-toastify';
import { useAuth } from '../auth/AuthProvider';

const REGION = 'us-central1';

function isSuperClaim(claims) {
  return (
    claims?.superAdmin === true ||
    claims?.superadmin === true ||
    String(claims?.role || '').toLowerCase().includes('super')
  );
}


async function forceFreshFirestoreAuth() {
  const auth = getAuth();
  if (!auth.currentUser) return;
  try {
    await auth.currentUser.getIdToken(true);
  } catch (err) {
    console.warn('token refresh failed before org read', err);
  }
}

function orgDisplayName(o) {
  return (
    o?.name ||
    o?.displayName ||
    o?.orgName ||
    o?._archMeta?.name ||
    o?._archMeta?.orgName ||
    o?._archMeta?.org?.name ||
    o?.id ||
    'Untitled org'
  );
}

function useOrgLists(enabled) {
  const db = getFirestore();
  const [orgs, setOrgs] = useState([]);
  const [archived, setArchived] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled));

  const load = useCallback(async () => {
    if (!enabled) {
      setOrgs([]);
      setArchived([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      await forceFreshFirestoreAuth();
      const orgSnap = await getDocs(query(collection(db, 'orgs'), orderBy('name')));
      const active = [];
      orgSnap.forEach((d) => active.push({ id: d.id, ...d.data() }));
      setOrgs(active);

      try {
        const archSnap = await getDocs(query(collection(db, 'archived_orgs'), orderBy('archivedAt', 'desc')));
        const arch = [];
        archSnap.forEach((d) => {
          const meta = d.data() || {};
          const org = meta.org || meta.snapshot || {};
          arch.push({ ...(org || {}), _archMeta: meta, id: d.id, orgId: d.id });
        });
        setArchived(arch);
      } catch (archErr) {
        console.error('archived_orgs read failed', archErr);
        setArchived([]);
      }

      setError(null);
    } catch (err) {
      console.error('orgs read failed', err);
      setOrgs([]);
      setArchived([]);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [db, enabled]);

  useEffect(() => {
    load();
  }, [load]);

  return { orgs, archived, error, loading, reload: load };
}

function ConfirmModal({ open, title, prompt, confirmLabel, confirmColor='bg-rose-600', value, setValue, onClose, onConfirm, disabled }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 px-4">
      <div className="w-full max-w-lg rounded-xl bg-[#0f1726] border border-white/10 p-5 text-white">
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-sm text-white/70 mb-4">{prompt}</p>
        <input
          className="w-full rounded-md bg-[#0b0f19] border border-white/10 px-3 py-2 text-sm mb-4"
          value={value}
          onChange={e => setValue(e.target.value)}
          autoFocus
        />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 rounded-md border border-white/10 hover:bg-white/5">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={disabled}
            className={`px-3 py-2 rounded-md ${confirmColor} disabled:opacity-60`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SuperOrgsList() {
  const nav = useNavigate();
  const { claims, refreshClaims } = useAuth();
  const isSuper = isSuperClaim(claims);
  const { orgs, archived, error, loading, reload } = useOrgLists(isSuper);
  const [search, setSearch] = useState('');
  const [refreshingAccess, setRefreshingAccess] = useState(false);

  const [confirm, setConfirm] = useState({ open: false, mode: null, org: null, text: '' });

  const fx = useMemo(() => getFunctions(undefined, REGION), []);
  const call = (name) => httpsCallable(fx, name);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return orgs;
    return orgs.filter(o =>
      o.name?.toLowerCase().includes(s) ||
      o.defaultApp?.toLowerCase().includes(s) ||
      o.id?.toLowerCase().includes(s)
    );
  }, [orgs, search]);

  const refreshAccess = async () => {
    setRefreshingAccess(true);
    try {
      await forceFreshFirestoreAuth();
      await refreshClaims({ force: true });
      await reload();
      toast.success('Access refreshed');
    } catch (e) {
      console.error(e);
      toast.error('Could not refresh access');
    } finally {
      setRefreshingAccess(false);
    }
  };

  const askArchive = (org) =>
    setConfirm({ open: true, mode: 'archive', org, text: '' });

  const askPurge = (org) =>
    setConfirm({ open: true, mode: 'purge', org, text: '' });

  const toggleDisabled = async (org) => {
    try {
      const next = !org.disabled;
      await call('setOrgDisabled')({ orgId: org.id, disabled: next });
      toast.success(next ? 'Organization disabled' : 'Organization enabled');
      await reload();
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to update status');
    }
  };

  const restore = async (org) => {
    try {
      await call('restoreOrgFromArchive')({ orgId: org.id });
      toast.success('Organization restored');
      await reload();
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Failed to restore');
    }
  };

  const confirmOk = () => confirm.text.trim() === orgDisplayName(confirm.org).trim();

  const runConfirm = async () => {
    if (!confirmOk()) return;
    try {
      if (confirm.mode === 'archive') {
        const db = getFirestore();
        const snap = await getDoc(doc(db, 'orgs', confirm.org.id));
        const org = snap.exists() ? snap.data() : confirm.org;

        await call('archiveOrg')({
          orgId: confirm.org.id,
          org: {
            name: org?.name || confirm.org?.name || null,
            defaultApp: org?.defaultApp || confirm.org?.defaultApp || null,
            province: org?.province || confirm.org?.province || null,
          },
        });

        toast.success('Organization archived');
      } else if (confirm.mode === 'purge') {
        await call('purgeArchivedOrg')({ orgId: confirm.org.id });
        toast.success('Organization permanently deleted');
      }
      setConfirm({ open: false, mode: null, org: null, text: '' });
      await reload();
    } catch (e) {
      console.error(e);
      toast.error(e.message || 'Action failed');
    }
  };

  return (
    <div className="super-orgs-page text-white">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="text-xl font-semibold">Organizations</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={refreshAccess}
            disabled={refreshingAccess}
            className="rounded-full border border-white/10 hover:bg-white/5 px-3 py-2 text-sm disabled:opacity-60"
          >
            {refreshingAccess ? 'Refreshing…' : 'Refresh access'}
          </button>
          <Link
            to="/super/orgs/new"
            className="rounded-full bg-cyan-600 hover:bg-cyan-500 px-3 py-2 text-sm"
          >
            + Create Organization
          </Link>
        </div>
      </div>

      <input
        placeholder="Search by name, default app, or orgId…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full mb-4 rounded-md bg-[#0b0f19] border border-white/10 px-3 py-2 text-sm"
      />

      {!isSuper && (
        <div className="mb-3 rounded-md bg-rose-900/30 border border-rose-700/40 p-3 text-rose-200 text-sm">
          Your current token does not contain Super Admin access yet. Click <b>Refresh access</b>, then sign out and sign back in if needed.
        </div>
      )}

      {error && (
        <div className="mb-3 rounded-md bg-amber-900/30 border border-amber-700/40 p-3 text-amber-100 text-sm">
          {error.code === 'permission-denied'
            ? 'Firestore denied the org list even though your account can reach Super Admin. Refresh access, then publish the latest Firestore rules if the issue remains.'
            : `Failed to load organizations: ${error.message || error.code}`}
        </div>
      )}

      {loading && (
        <div className="mb-4 rounded-md border border-white/10 bg-[#0f1726] px-4 py-3 text-sm text-white/70">
          Loading organizations…
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((o) => (
          <div
            key={o.id}
            className="rounded-lg border border-white/10 bg-[#0f1726] px-4 py-3 flex items-center justify-between"
          >
            <div>
              <div className="font-medium">{orgDisplayName(o)}</div>
              <div className="text-xs text-white/60">
                Default app: {o.defaultApp || '—'}
                {' · '}Province: {o.province || '—'}
                {' · '}
                <span className={o.disabled ? 'text-amber-400' : 'text-emerald-400'}>
                  {o.disabled ? 'DISABLED' : 'ACTIVE'}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => nav(`/super/orgs/${o.id}`)}
                className="px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 text-sm"
              >
                Open
              </button>
              <button
                onClick={() => askArchive(o)}
                className="px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-500 text-sm"
              >
                Archive
              </button>
              <button
                onClick={() => toggleDisabled(o)}
                className="px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 text-sm"
              >
                {o.disabled ? 'Enable' : 'Disable'}
              </button>
              <button
                disabled
                title="Permanent delete is only available from Archive"
                className="px-3 py-1.5 rounded-md bg-rose-900/40 text-rose-300/60 cursor-not-allowed text-sm"
              >
                Delete
              </button>
            </div>
          </div>
        ))}

        {!loading && !filtered.length && !error && (
          <div className="rounded-md border border-white/10 bg-[#0f1726] px-4 py-6 text-sm text-white/60">
            No organizations found.
          </div>
        )}
      </div>

      <div className="mt-8">
        <h3 className="text-xl font-semibold mb-3">Archived</h3>
        <div className="space-y-3">
          {archived.map((o) => (
            <div
              key={o.id}
              className="rounded-lg border border-white/10 bg-[#0f1726] px-4 py-3 flex items-center justify-between"
            >
              <div>
                <div className="font-medium">{orgDisplayName(o)}</div>
                <div className="text-xs text-white/60">
                  Archived {o?._archMeta?.archivedAt?.toDate ? o._archMeta.archivedAt.toDate().toLocaleString() : ''}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => restore(o)}
                  className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-sm"
                >
                  Restore
                </button>
                <button
                  onClick={() => askPurge(o)}
                  className="px-3 py-1.5 rounded-md bg-rose-600 hover:bg-rose-500 text-sm"
                >
                  Delete permanently
                </button>
              </div>
            </div>
          ))}

          {!loading && !archived.length && (
            <div className="text-white/60 text-sm">No archived organizations.</div>
          )}
        </div>
      </div>

      <ConfirmModal
        open={confirm.open}
        title={confirm.mode === 'archive' ? 'Archive organization' : 'Delete permanently'}
        prompt={`Type "${orgDisplayName(confirm.org)}" to ${confirm.mode === 'archive' ? 'archive' : 'permanently delete'} this organization.`}
        confirmLabel={confirm.mode === 'archive' ? 'Archive' : 'Delete permanently'}
        confirmColor={confirm.mode === 'archive' ? 'bg-amber-600' : 'bg-rose-600'}
        value={confirm.text}
        setValue={(text) => setConfirm((s) => ({ ...s, text }))}
        onClose={() => setConfirm({ open: false, mode: null, org: null, text: '' })}
        onConfirm={runConfirm}
        disabled={!confirmOk()}
      />
    </div>
  );
}
